import { useCallback, useEffect, useMemo, useState } from 'react'
import { $createLinkNode, $isAutoLinkNode, $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $getSelectionStyleValueForProperty, $isAtNodeEnd, $patchStyleText } from '@lexical/selection'
import type { ElementFormatType, TextFormatType, RangeSelection, ElementNode, TextNode } from 'lexical'
import {
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
  REDO_COMMAND,
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
} from 'lexical'

import { FONT_FAMILY_OPTIONS, FONT_SIZE_OPTIONS } from './text-toolbar.consts'

export const useTextToolbarModel = () => {
  const [editor] = useLexicalComposerContext()
  const [fontFamily, setFontFamily] = useState(FONT_FAMILY_OPTIONS[0].value)
  const [fontSize, setFontSize] = useState(FONT_SIZE_OPTIONS[5].value)
  const [textColor, setTextColor] = useState('#000000')
  const [highlightColor, setHighlightColor] = useState('')
  const [isBold, setIsBold] = useState(false)
  const [isItalic, setIsItalic] = useState(false)
  const [isUnderline, setIsUnderline] = useState(false)
  const [alignment, setAlignment] = useState<ElementFormatType>('left')
  const [isLink, setIsLink] = useState(false)
  const [isLinkEditMode, setIsLinkEditMode] = useState(false)
  const [linkValue, setLinkValue] = useState('')
  const [isHighlightModalOpen, setIsHighlightModalOpen] = useState(false)
  const [isColorModalOpen, setIsColorModalOpen] = useState(false)

  const getStyleValue = (property: string, defaultValue: string) => {
    let value = defaultValue
    editor.getEditorState().read(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        value = $getSelectionStyleValueForProperty(selection, property, defaultValue)
      }
    })
    return value
  }

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        const selection = $getSelection()

        if ($isRangeSelection(selection)) {
          const hasUnderline =
            selection.hasFormat('underline') ||
            selection.getNodes().some(node => {
              if ($isTextNode(node)) {
                return node.getStyle().includes('underline')
              }
              return false
            })

          setIsUnderline(hasUnderline)
          setIsBold(selection.hasFormat('bold'))
          setIsItalic(selection.hasFormat('italic'))

          setFontFamily(getStyleValue('font-family', 'Muli'))
          setFontSize(getStyleValue('font-size', '16px'))
          setTextColor(getStyleValue('color', '#000000'))
          setHighlightColor(getStyleValue('background-color', ''))

          const node = getSelectedNode(selection)
          const parent = node.getParent()
          const isLink = $isLinkNode(parent) || $isLinkNode(node)
          setIsLink(isLink)
          if ($isLinkNode(parent)) {
            setLinkValue(parent.getURL())
          } else if ($isLinkNode(node)) {
            setLinkValue(node.getURL())
          } else {
            setLinkValue('')
          }
        }
        return false
      },
      0
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor])

  const applyStyleText = useCallback(
    (styles: Record<string, string>) => {
      editor.update(() => {
        const selection = $getSelection()
        if ($isRangeSelection(selection)) {
          $patchStyleText(selection, styles)
        }
      })
    },
    [editor]
  )

  const handleFontFamilyChange = useCallback(
    (value: string) => {
      setFontFamily(value)
      applyStyleText({ 'font-family': value })
    },
    [applyStyleText]
  )

  const handleFontSizeChange = useCallback(
    (value: string) => {
      setFontSize(value)
      applyStyleText({ 'font-size': value })
    },
    [applyStyleText]
  )

  const handleHighlightChange = (color: string) => {
    setHighlightColor(color)
    applyStyleText({ 'background-color': color })
  }

  const handleColorChange = (color: string) => {
    setTextColor(color)
    applyStyleText({ color })
  }

  const handleAlignment = (align: ElementFormatType) => {
    setAlignment(align)
    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, align)
  }

  const handleUndo = () => editor.dispatchCommand(UNDO_COMMAND, undefined)

  const handleRedo = () => editor.dispatchCommand(REDO_COMMAND, undefined)

  const formatToState = {
    italic: setIsItalic,
    bold: setIsBold,
    underline: setIsUnderline,
  }

  const handleFormat = (type: TextFormatType) => {
    const stateSetter = formatToState?.[type]
    if (stateSetter) {
      stateSetter(prevState => !prevState)
    }
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, type)
  }

  const openLinkModal = () => {
    setIsLinkEditMode(true)
  }

  function getSelectedNode(selection: RangeSelection): TextNode | ElementNode {
    const anchor = selection.anchor
    const focus = selection.focus
    const anchorNode = selection.anchor.getNode()
    const focusNode = selection.focus.getNode()
    if (anchorNode === focusNode) {
      return anchorNode
    }
    const isBackward = selection.isBackward()
    if (isBackward) {
      return $isAtNodeEnd(focus) ? anchorNode : focusNode
    } else {
      return $isAtNodeEnd(anchor) ? anchorNode : focusNode
    }
  }

  const submitLink = () => {
    editor.update(() => {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, linkValue)
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        const parent = getSelectedNode(selection).getParent()
        if ($isAutoLinkNode(parent)) {
          const linkNode = $createLinkNode(parent.getURL(), {
            rel: parent.__rel,
            target: parent.__target,
            title: parent.__title,
          })
          parent.replace(linkNode, true)
        }
      }
    })
    setIsLink(true)
    setIsLinkEditMode(false)
  }

  const handleCloseLinkModal = () => {
    setIsLinkEditMode(false)
  }

  const onLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setLinkValue(value)
  }

  const fontFamilyOptions = useMemo(() => {
    return FONT_FAMILY_OPTIONS.map(item => {
      const isMuli = item.value === 'Muli'
      const isMuliSelected = ['Muli', 'inherit'].includes(fontFamily)
      const isBasicSelected = fontFamily === item.value
      const isSelected = isMuli ? isMuliSelected : isBasicSelected
      return {
        label: item.label,
        onClick: () => handleFontFamilyChange(item.value),
        isSelected,
      }
    })
  }, [fontFamily, handleFontFamilyChange])

  const fontSizeOptions = useMemo(() => {
    return FONT_SIZE_OPTIONS.map(item => {
      return {
        label: item.label,
        onClick: () => handleFontSizeChange(item.value),
        isSelected: fontSize === item.value,
      }
    })
  }, [fontSize, handleFontSizeChange])

  return {
    handleColorChange,
    handleAlignment,
    textColor,
    isBold,
    isItalic,
    isUnderline,
    alignment,
    handleUndo,
    handleRedo,
    handleFormat,
    openLinkModal,
    isLinkEditMode,
    linkValue,
    onLinkChange,
    submitLink,
    fontFamilyOptions,
    fontSizeOptions,
    handleHighlightChange,
    highlightColor,
    isLink,
    isHighlightModalOpen,
    isColorModalOpen,
    handleHighlightModal: setIsHighlightModalOpen,
    handleColorModal: setIsColorModalOpen,
    handleCloseLinkModal,
  }
}
